import { Badge, Button } from "@design";
import { useQuery } from "@tanstack/react-query";
import { FileText, Lock, Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { listSpaceKnowledgeOptions, type SpaceHubResponse } from "@/api";
import { QuerySection } from "@/components/section";
import { Text } from "@/components/Text";
import { useCapabilities } from "@/features/account/useCurrentUser";
import { knowledgePath } from "@/features/knowledge/KnowledgeTabs";
import { formatList, formatRelativeTime } from "@/i18n/format";
import { RailBlock } from "./RailBlock";
import { WikiRailBlock } from "./WikiRailBlock";

function LatestKnowledge({ spaceUuid }: Readonly<{ spaceUuid: string }>) {
  const { t } = useTranslation("spaces", { keyPrefix: "detail.knowledge" });
  const navigate = useNavigate();
  // With the wiki on, the space's Knowledge area has Wiki and Files: this block is the files.
  const { knowledgeGraphEnabled } = useCapabilities();
  const files = useQuery({ ...listSpaceKnowledgeOptions({ path: { space_uuid: spaceUuid } }), select: (r) => r.data });
  return (
    <RailBlock
      title={knowledgeGraphEnabled ? t("filesTitle") : t("title")}
      action={
        <div className="-mr-3">
          <Button variant="text" size="sm" onClick={() => navigate(knowledgePath(spaceUuid, "files"))}>
            {t("all")}
          </Button>
        </div>
      }
    >
      <QuerySection query={files} isEmpty={(list) => list.length === 0} empty={{ hint: t("empty") }} loadingRows={2}>
        {(list) => (
          <ul className="flex flex-col">
            {[...list]
              .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
              .slice(0, 4)
              .map((file) => (
                <li key={file.uuid} className="flex items-center gap-2.5 py-1.5">
                  <FileText size={14} aria-hidden className="shrink-0 text-content-tertiary" />
                  <div className="min-w-0 flex-1">
                    <Text variant="body-sm" className="truncate">
                      {file.filename}
                    </Text>
                    <Text variant="caption" tone="tertiary">
                      {formatRelativeTime(file.createdAt)}
                    </Text>
                  </div>
                </li>
              ))}
          </ul>
        )}
      </QuerySection>
    </RailBlock>
  );
}

/** About, who can see the space, and its latest files (shared spaces only). */
export function SpaceAboutRail({ space }: Readonly<{ space: SpaceHubResponse }>) {
  const { t } = useTranslation("spaces", { keyPrefix: "detail" });
  const teams = space.teamNames ?? [];
  let visibility = { label: t("visibility.members"), body: t("visibility.membersBody") };
  if (space.isPersonal) visibility = { label: t("visibility.private"), body: t("visibility.privateBody") };
  else if (teams.length > 0)
    visibility = { label: t("visibility.team"), body: t("visibility.teamBody", { teams: formatList(teams) }) };

  return (
    <aside className="flex flex-col divide-y divide-border-subtle">
      <RailBlock title={t("about.title")}>
        <Text variant="body-sm" tone="secondary">
          {space.isPersonal ? t("about.privateBody") : space.description || t("about.none")}
        </Text>
      </RailBlock>
      <RailBlock title={t("visibility.title")}>
        <div>
          <Badge
            variant={space.isPersonal ? "neutral" : "info"}
            icon={space.isPersonal ? <Lock size={12} /> : <Users size={12} />}
          >
            {visibility.label}
          </Badge>
        </div>
        <Text variant="caption" tone="tertiary">
          {visibility.body}
        </Text>
      </RailBlock>
      <WikiRailBlock spaceUuid={space.uuid} />
      {!space.isPersonal && <LatestKnowledge spaceUuid={space.uuid} />}
    </aside>
  );
}
